// Sprint CFG.SEC CFG.PA.A3.UI (issue #758) — Frontend MFA enrollment.
//
// Wraps the existing backend endpoints shipped in PR #756:
//
//   GET  /auth/platform/mfa/status — read-only enrollment-state probe
//                                    (added in this PR — see
//                                    routes/platform-auth.ts).
//   POST /auth/platform/mfa/setup  — generates fresh { secret, otpauthUri }
//   POST /auth/platform/mfa/verify — persists secret on valid TOTP
//
// Threat model the page is built against (Codex review tells us to think
// about these up front):
//
//   * QR code rendering from a server-controlled URI: we use qrcode.react
//     which renders SVG via React primitives. No dangerouslySetInnerHTML
//     anywhere. The otpauthUri is fed to the library as a string `value`
//     prop and never injected into the DOM as HTML.
//
//   * Secret residue in JS memory: as soon as enrollment succeeds we
//     null the secret state so post-success React DevTools / heap dumps
//     don't expose it. The QR + manual secret are no longer rendered.
//
//   * Race between /setup pending and verify submission: the verify
//     button is disabled until {secret, otpauthUri} are in state, and
//     while a verify request is in flight.
//
//   * Auth bypass via direct URL: ProtectedRoute redirects unauthenticated
//     users; the backend independently enforces full-session JWT +
//     platform_admins row, so the page can't render the setup form for
//     a non-admin even if they somehow get past the route guard.
//
// Re-enrollment / disable flows are out of scope (per issue #758) — admins
// who need to rotate their TOTP secret use the existing curl path that
// supplies current_token. This page only handles first-time enrollment.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { HelpPopover } from '../components/help';

interface SetupResponse {
  secret:     string;
  otpauthUri: string;
}

interface StatusResponse {
  enrolled: boolean;
}

type PageState =
  // Initial GET /status in flight.
  | { kind: 'loading' }
  // 403 from GET /status — caller isn't a platform admin (or session
  // type is rejected). Stops here with an explanatory message.
  | { kind: 'not_admin' }
  // mfa_secret already on file. Show "already enrolled" state — user
  // must use the curl re-enrollment path or contact a SUPER_ADMIN.
  | { kind: 'already_enrolled' }
  // Setup secret + otpauthUri loaded. User scans QR / enters token.
  | { kind: 'setup_ready'; secret: string; otpauthUri: string }
  // Verify success — secret has been cleared from state above; we
  // only keep enough to render the success message + CTA.
  | { kind: 'success' }
  // Page-level fatal error (e.g. /setup itself failed). Distinct from
  // a wrong-TOTP retry, which is handled inside setup_ready.
  | { kind: 'error'; message: string };

// ── Styling primitives — match LoginPage's inline-CSS-vars convention. ──

const inputStyle: React.CSSProperties = {
  display:      'block',
  width:        '100%',
  height:       44,
  background:   'var(--surface-alt)',
  border:       '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding:      '0 14px',
  fontFamily:   'var(--font-body)',
  fontSize:     14,
  color:        'var(--text)',
  outline:      'none',
  boxSizing:    'border-box',
  letterSpacing: '0.2em',
  textAlign:    'center',
};

const buttonStyle = (loading: boolean): React.CSSProperties => ({
  display:      'block',
  width:        '100%',
  height:       44,
  background:   'var(--primary)',
  color:        'white',
  fontFamily:   'var(--font-display)',
  fontSize:     15,
  fontWeight:   600,
  borderRadius: 'var(--radius-sm)',
  border:       'none',
  cursor:       loading ? 'not-allowed' : 'pointer',
  opacity:      loading ? 0.7 : 1,
  transition:   'box-shadow 150ms',
});

export default function PlatformMfaSetupPage() {
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [token, setToken] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  // The closure-scoped `cancelled` flag below ignores in-flight responses
  // if the component unmounts mid-fetch (StrictMode mounts twice in dev,
  // and a slow /setup could resolve after the user navigates away).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await apiFetch<StatusResponse>('/auth/platform/mfa/status');
        if (cancelled) return;
        if (status.enrolled) {
          setState({ kind: 'already_enrolled' });
          return;
        }
        // Not enrolled — kick off setup. We deliberately POST /setup
        // here rather than on a button click so the QR is visible the
        // moment the page renders (matches the issue's UX flow).
        const setup = await apiFetch<SetupResponse>('/auth/platform/mfa/setup', {
          method: 'POST',
        });
        if (cancelled) return;
        setState({ kind: 'setup_ready', secret: setup.secret, otpauthUri: setup.otpauthUri });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unexpected error';
        // The /status endpoint surfaces 'PLATFORM_ADMIN_REQUIRED' as
        // its error string when the route middleware rejects (also
        // covers 'Action-scoped session cannot access this resource').
        // Treat all of those as "not a platform admin via full
        // session" and show the same not-admin state — leaking the
        // exact reason wouldn't help the legitimate user.
        if (
          message.includes('PLATFORM_ADMIN_REQUIRED') ||
          message.includes('Action-scoped session')
        ) {
          setState({ kind: 'not_admin' });
        } else {
          setState({ kind: 'error', message });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    // The submit button is disabled when these conditions are violated,
    // but the form still submits on Enter inside the input regardless.
    // Bail out client-side rather than round-tripping a malformed token.
    if (state.kind !== 'setup_ready' || verifying || token.trim().length !== 6) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      await apiFetch<{ enrolled: true }>('/auth/platform/mfa/verify', {
        method: 'POST',
        body:   JSON.stringify({
          proposed_secret: state.secret,
          token:           token.trim(),
        }),
      });
      // Wipe the secret from state on success — past this point the
      // QR / manual secret should NOT be re-renderable.
      setState({ kind: 'success' });
      setToken('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      // INVALID_TOKEN is the only retry-friendly error — the secret
      // stays valid in state, the user can fix the typo and resubmit.
      // Any other error from /verify is unexpected; surface it but
      // don't tear down the setup (admin can refresh if they want a
      // fresh secret).
      setVerifyError(
        message.includes('INVALID_TOKEN')
          ? 'That code didn’t match. Check your authenticator and try again.'
          : message,
      );
    } finally {
      setVerifying(false);
    }
  }

  async function handleCopySecret() {
    if (state.kind !== 'setup_ready') return;
    try {
      await navigator.clipboard.writeText(state.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts or restricted
      // iframes — silently ignore; the secret is still visible for
      // manual selection.
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div
        style={{
          width:        '100%',
          maxWidth:     460,
          background:   'var(--surface)',
          border:       '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          boxShadow:    'var(--shadow-lg)',
          padding:      40,
        }}
      >
        <Header />
        <PageBody
          state={state}
          token={token}
          setToken={setToken}
          verifying={verifying}
          verifyError={verifyError}
          copied={copied}
          onVerify={handleVerify}
          onCopy={handleCopySecret}
        />
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="flex flex-col items-center mb-6">
      <div
        style={{
          display:        'inline-flex',
          alignItems:     'center',
          gap:            8,
        }}
      >
        <div
          style={{
            fontFamily:    'var(--font-display)',
            fontSize:      20,
            fontWeight:    700,
            color:         'var(--text)',
            letterSpacing: '-0.5px',
          }}
        >
          Platform Admin MFA
        </div>
        {/* Contextual help — explains why MFA is required and what the
            user should do to keep their authenticator recoverable on a
            new device. Rendered alongside the heading for every
            PageState so the help is reachable from any state of the page.
            Body content matches the page's actual behaviour: setup
            issues a manual TOTP secret (no recovery-code generation)
            and the secret is the user's only re-enrolment path. */}
        <HelpPopover
          id="mfa-enrollment-overview"
          variant="info"
          heading="Why MFA is required"
          body={
            <>
              <p style={{ margin: '0 0 8px' }}>
                Multi-factor authentication is required for all platform admins. It adds a second verification step beyond your password, using an authenticator app on your phone.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                Once enabled, you&apos;ll need both your password and a 6-digit code from your authenticator app every time you sign in to platform-admin functions.
              </p>
              <p style={{ margin: 0 }}>
                Save the manual TOTP secret shown during setup somewhere safe. If you lose your phone, this secret is how you re-enrol the authenticator on a new device. We can&apos;t recover the secret for you after setup completes.
              </p>
            </>
          }
        />
      </div>
      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize:   12,
          color:      'var(--text-secondary)',
          marginTop:  4,
        }}
      >
        Two-factor enrollment (TOTP)
      </p>
    </div>
  );
}

interface BodyProps {
  state:        PageState;
  token:        string;
  setToken:     (v: string) => void;
  verifying:    boolean;
  verifyError:  string | null;
  copied:       boolean;
  onVerify:     (e: React.FormEvent) => void;
  onCopy:       () => void;
}

function PageBody(props: BodyProps) {
  const { state } = props;
  switch (state.kind) {
    case 'loading':
      return (
        <p
          data-testid="mfa-loading"
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center' }}
        >
          Loading…
        </p>
      );
    case 'not_admin':
      return (
        <Alert
          testid="mfa-not-admin"
          variant="error"
          title="Not a platform admin"
          body="This page is only available to platform administrators. If you believe this is an error, contact a SUPER_ADMIN."
        />
      );
    case 'already_enrolled':
      return (
        <Alert
          testid="mfa-already-enrolled"
          variant="info"
          title="MFA already enrolled"
          body="This account already has TOTP enrollment on file. To rotate your secret, contact a SUPER_ADMIN or use the existing rotation flow."
        />
      );
    case 'success':
      return (
        <div data-testid="mfa-success">
          <Alert
            variant="success"
            title="Enrollment complete"
            body="Your authenticator is now linked to this platform admin account. Use it the next time you sign in."
          />
          <Link
            to="/platform/login"
            style={{
              display:        'block',
              width:          '100%',
              height:         44,
              background:     'var(--primary)',
              color:          'white',
              fontFamily:     'var(--font-display)',
              fontSize:       15,
              fontWeight:     600,
              borderRadius:   'var(--radius-sm)',
              textDecoration: 'none',
              textAlign:      'center',
              lineHeight:     '44px',
              marginTop:      16,
            }}
          >
            Continue to Platform Login
          </Link>
        </div>
      );
    case 'error':
      return (
        <Alert
          testid="mfa-error"
          variant="error"
          title="Couldn’t start MFA enrollment"
          body={state.message}
        />
      );
    case 'setup_ready':
      return <SetupForm {...props} state={state} />;
  }
}

function SetupForm({
  state,
  token,
  setToken,
  verifying,
  verifyError,
  copied,
  onVerify,
  onCopy,
}: BodyProps & { state: Extract<PageState, { kind: 'setup_ready' }> }) {
  return (
    <form onSubmit={onVerify} data-testid="mfa-setup-form">
      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize:   13,
          color:      'var(--text)',
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Scan the QR code with your authenticator app (1Password, Authy,
        Google Authenticator). If you can’t scan, enter the secret manually.
      </p>

      <div
        data-testid="mfa-qr"
        style={{
          display:        'flex',
          justifyContent: 'center',
          padding:        16,
          background:     'white',
          borderRadius:   'var(--radius-sm)',
          marginBottom:   16,
        }}
      >
        <QRCodeSVG value={state.otpauthUri} size={192} level="M" />
      </div>

      <label
        style={{
          display: 'block',
          fontFamily: 'var(--font-body)',
          fontSize:   13,
          fontWeight: 500,
          color:      'var(--text)',
          marginBottom: 6,
        }}
      >
        Manual secret
      </label>
      <div
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            8,
          background:     'var(--surface-alt)',
          border:         '1px solid var(--border)',
          borderRadius:   'var(--radius-sm)',
          padding:        '10px 14px',
          marginBottom:   12,
        }}
      >
        <code
          data-testid="mfa-secret"
          style={{
            flex:       1,
            fontFamily: 'var(--font-mono, monospace)',
            fontSize:   13,
            color:      'var(--text)',
            wordBreak:  'break-all',
            userSelect: 'all',
          }}
        >
          {state.secret}
        </code>
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copy secret"
          style={{
            display:      'inline-flex',
            alignItems:   'center',
            gap:          4,
            background:   'transparent',
            border:       '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding:      '4px 10px',
            fontSize:     12,
            color:        'var(--text-secondary)',
            cursor:       'pointer',
          }}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize:   12,
          color:      'var(--text-secondary)',
          background: 'rgba(245,158,11,0.08)',
          border:     '1px solid rgba(245,158,11,0.25)',
          borderRadius: 'var(--radius-sm)',
          padding:    '8px 12px',
          marginBottom: 20,
          lineHeight: 1.5,
        }}
      >
        <strong>Save this secret</strong> in your password manager or on paper
        before continuing. If you lose your authenticator device and don’t
        have this secret, an admin will need to reset your MFA.
      </p>

      <label
        style={{
          display:    'block',
          fontFamily: 'var(--font-body)',
          fontSize:   13,
          fontWeight: 500,
          color:      'var(--text)',
          marginBottom: 6,
        }}
      >
        6-digit verification code
      </label>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="\d{6}"
        maxLength={6}
        required
        value={token}
        onChange={e => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
        style={inputStyle}
        data-testid="mfa-token-input"
        aria-invalid={verifyError ? true : undefined}
      />

      {verifyError && (
        <div
          data-testid="mfa-verify-error"
          role="alert"
          style={{
            fontFamily:   'var(--font-body)',
            fontSize:     13,
            color:        'var(--error)',
            background:   'rgba(239,68,68,0.06)',
            border:       '1px solid rgba(239,68,68,0.2)',
            borderRadius: 'var(--radius-sm)',
            padding:      '8px 12px',
            marginTop:    12,
          }}
        >
          {verifyError}
        </div>
      )}

      <button
        type="submit"
        disabled={verifying || token.length !== 6}
        style={{ ...buttonStyle(verifying || token.length !== 6), marginTop: 20 }}
        data-testid="mfa-verify-button"
      >
        {verifying ? 'Verifying…' : 'Verify and enable MFA'}
      </button>
    </form>
  );
}

interface AlertProps {
  testid?: string;
  variant: 'success' | 'error' | 'info';
  title:   string;
  body:    string;
}

function Alert({ testid, variant, title, body }: AlertProps) {
  const palette = {
    success: { bg: 'rgba(34,197,94,0.08)',  border: 'rgba(34,197,94,0.25)',  fg: 'var(--text)' },
    error:   { bg: 'rgba(239,68,68,0.06)',  border: 'rgba(239,68,68,0.2)',   fg: 'var(--text)' },
    info:    { bg: 'rgba(124,58,237,0.06)', border: 'rgba(124,58,237,0.25)', fg: 'var(--text)' },
  }[variant];
  return (
    <div
      data-testid={testid}
      style={{
        fontFamily:   'var(--font-body)',
        fontSize:     13,
        background:   palette.bg,
        border:       `1px solid ${palette.border}`,
        borderRadius: 'var(--radius-sm)',
        padding:      '12px 14px',
        color:        palette.fg,
        lineHeight:   1.5,
      }}
    >
      <strong style={{ display: 'block', marginBottom: 4 }}>{title}</strong>
      {body}
    </div>
  );
}
